/**
 * ProviderQueuePanel — My Day left sidebar: Queue + Schedule view
 */
import React from 'react';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { STATUS_INDICATORS } from './constants';

export function ProviderQueuePanel({ rooms, collapsed, onToggle, sideView, onSwitchView, onPatientClick, onForceStart }) {
  // BUG FIX: "ready_for_provider" maps to indicator="ready" after backend fix.
  // Also include "pending" so scheduled/checked-in patients show in schedule view.
  const queueItems = rooms.filter(r => r.indicator === 'ready');
  const scheduleItems = rooms;

  // Build time-slot schedule view
  const HOURS = Array.from({ length: 10 }, (_, i) => 8 + i); // 8am-5pm

  return (
    <div className={`bg-white border-r border-slate-200 flex flex-col transition-all duration-200 shadow-sm
      ${collapsed ? 'w-11' : 'w-60'}`}
      data-testid="provider-queue-panel">
      {/* Header with toggle */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        {!collapsed && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {sideView === 'queue' ? 'Provider Queue' : 'Schedule'}
          </span>
        )}
        <button onClick={onToggle} className="p-1 rounded hover:bg-slate-100 transition-colors" data-testid="panel-toggle-btn">
          {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" /> : <ChevronLeft className="w-3.5 h-3.5 text-slate-400" />}
        </button>
      </div>

      {/* View switcher — 7O.5 */}
      {!collapsed && (
        <div className="flex mx-2 mt-2 p-0.5 rounded-lg bg-slate-100" data-testid="side-view-toggle">
          {[{ key: 'queue', label: 'Queue' }, { key: 'schedule', label: 'Schedule' }].map(v => (
            <button key={v.key} onClick={() => onSwitchView(v.key)}
              className={`flex-1 py-1 text-[10px] font-medium rounded-md transition-all ${sideView === v.key ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
              data-testid={`side-view-${v.key}`}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto mt-1">
        {collapsed ? (
          /* Collapsed: just dots */
          rooms.slice(0, 10).map(room => {
            const ind = STATUS_INDICATORS[room.indicator] || STATUS_INDICATORS.not_arrived;
            return (
              <button key={room.id} onClick={() => onPatientClick(room)}
                className="w-full flex justify-center py-2 hover:bg-slate-50"
                title={`${room.patient_name} — ${ind.label}`}>
                <span className={`w-2 h-2 rounded-full ${ind.dot}`} />
              </button>
            );
          })
        ) : sideView === 'queue' ? (
          /* 7Q.3: Provider Queue — island cards */
          queueItems.length === 0 ? (
            <p className="text-[10px] text-slate-400 px-3 py-4 text-center">No patients in queue</p>
          ) : (
            <div className="px-2 space-y-1.5 py-1.5">
              {queueItems.map(room => {
                const ind = STATUS_INDICATORS[room.indicator] || STATUS_INDICATORS.not_arrived;
                return (
                  <button key={room.id} onClick={() => onPatientClick(room)}
                    className="w-full text-left px-3 py-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all group"
                    data-testid={`queue-item-${room.id}`}>
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${ind.dot} shadow-sm`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-semibold text-slate-700 truncate">{room.patient_name}</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">{room.time} · {room.visit_type || 'Visit'}</p>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                );
              })}
            </div>
          )
        ) : (
          /* 7O.4: Schedule View — compact day timeline with "Start Directly" override */
          <div className="px-1" data-testid="schedule-view">
            {HOURS.map(hour => {
              const timeStr = `${hour.toString().padStart(2, '0')}:00`;
              const patientsAtHour = scheduleItems.filter(r => r.time?.startsWith(`${hour.toString().padStart(2, '0')}:`));
              const isLunch = hour === 12;

              if (isLunch && patientsAtHour.length === 0) {
                return (
                  <div key={hour} className="px-2 py-1 text-[9px] text-slate-300 italic text-center border-b border-slate-50">
                    Lunch Break
                  </div>
                );
              }

              if (patientsAtHour.length === 0) return null;

              return (
                <div key={hour}>
                  <div className="px-2 pt-1.5 pb-0.5 text-[9px] text-slate-300 font-medium tabular-nums">{timeStr}</div>
                  {patientsAtHour.map(room => {
                    const ind = STATUS_INDICATORS[room.indicator] || STATUS_INDICATORS.not_arrived;
                    const canStart = ['ready', 'pending', 'not_arrived'].includes(room.indicator) && !room.encounter_id;
                    const inVisit = room.indicator === 'ready' && room.encounter_id;
                    const isComplete = room.indicator === 'completed';
                    return (
                      <div key={room.id} className="group">
                        <button onClick={() => onPatientClick(room)}
                          className="w-full text-left px-2 py-1.5 hover:bg-slate-50 rounded transition-colors"
                          data-testid={`schedule-item-${room.id}`}>
                          <div className="flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ind.dot}`} />
                            <span className="text-[11px] font-medium text-slate-600 truncate flex-1">{room.patient_name}</span>
                            {isComplete && <span className="text-[8px] text-slate-300">done</span>}
                          </div>
                        </button>
                        {/* "Start Directly" override — shown for non-complete, non-in-visit patients */}
                        {!isComplete && onForceStart && (
                          <button
                            onClick={() => onForceStart(room)}
                            className="opacity-0 group-hover:opacity-100 w-full text-left pl-6 pr-2 py-0.5 text-[9px] text-sky-600 hover:text-sky-800 font-medium transition-all"
                            data-testid={`force-start-${room.id}`}>
                            {inVisit ? '→ Resume visit' : '→ Start directly'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {scheduleItems.length === 0 && (
              <p className="text-[10px] text-slate-400 px-3 py-4 text-center">No appointments today</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   BUCKET BAR
   ═══════════════════════════════════════ */

